import { useCallback, useRef } from 'react';

import { useSyncSmartFolder } from './mutations/useSyncSmartFolder.ts';
import { useFolderSyncActions } from './SmartFolderSyncContext.tsx';

export type SmartFolderSyncStatus = 'idle' | 'syncing' | 'success' | 'error';

type UseSmartFolderSyncOptions = {
	/** Called after a successful sync so consumers can refresh their data (e.g. page tree, folder view). */
	onSyncComplete?: () => void;
};

type UseSmartFolderSyncResult = {
	/** Trigger a sync for the given folder. Safe to call multiple times – concurrent calls for the same folder are de-duplicated. */
	triggerSync: (folderId: string) => void;
	/** Current sync status. */
	status: SmartFolderSyncStatus;
};

/**
 * Hook that triggers a smart-folder sync and exposes the current sync status.
 *
 * Designed to be called when a user opens a folder view or navigates to
 * content within a smart folder. Duplicate concurrent calls for the same
 * folder are de-duplicated. The syncing folder ID is published via
 * SmartFolderSyncContext so other components (e.g. the page tree) can show a
 * loading indicator.
 */
export const useSmartFolderSync = (
	options?: UseSmartFolderSyncOptions,
): UseSmartFolderSyncResult => {
	const { onSyncComplete } = options ?? {};
	const { syncSmartFolder, loading } = useSyncSmartFolder();
	const { addSyncingFolder, removeSyncingFolder } = useFolderSyncActions();
	const syncingFolderRef = useRef<string | null>(null);
	const statusRef = useRef<SmartFolderSyncStatus>('idle');
	// Derive a simple status from the mutation state + our local tracking
	const status: SmartFolderSyncStatus = loading ? 'syncing' : statusRef.current;

	const triggerSync = useCallback(
		(folderId: string) => {
			// De-duplicate: don't fire another request if we're already syncing this folder
			if (syncingFolderRef.current === folderId) {
				return;
			}

			syncingFolderRef.current = folderId;
			statusRef.current = 'syncing';
			addSyncingFolder(folderId);

			syncSmartFolder({ folderId })
				.then((result) => {
					const success = result?.data?.confluence_syncSmartFolder?.success ?? false;
					statusRef.current = success ? 'success' : 'error';

					if (success) {
						onSyncComplete?.();
					}
				})
				.catch(() => {
					statusRef.current = 'error';
				})
				.finally(() => {
					const finishedId = syncingFolderRef.current;
					syncingFolderRef.current = null;
					if (finishedId) {
						removeSyncingFolder(finishedId);
					}
					statusRef.current = 'idle';
				});
		},
		[syncSmartFolder, onSyncComplete, addSyncingFolder, removeSyncingFolder],
	);

	return { triggerSync, status };
};
